import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthShell from '../../components/auth/AuthShell.tsx';
import { authConfig } from '../../config/auth-config.ts';
import './Approve.css';
import { requestTimeout } from '../../config/requestTimeout.ts';

type Phase = 'sending' | 'approved' | 'expired' | 'invalid' | 'error';

function readToken(params: URLSearchParams): string | undefined {
  const raw = params.get('token') ?? '';
  if (/^[0-9a-f-]{36}$/i.test(raw)) {
    return raw;
  }
  return undefined;
}

export default function Approve() {
  const [params] = useSearchParams();
  const token = readToken(params);
  const [phase, setPhase] = useState<Phase>(token === undefined ? 'invalid' : 'sending');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token === undefined) {
      return;
    }
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        const res = await fetch(authConfig.authApproveUrl, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ token }),
          signal: requestTimeout(),
  });
        if (cancelled) {return;}
        if (res.status === 200) {
          setPhase('approved');
          return;
        }
        if (res.status === 410) {
          setPhase('expired');
          return;
        }
        // Anything else is unexpected — surface the status.
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(`server returned ${res.status}: ${body.error ?? 'unknown'}`);
        setPhase('error');
      } catch (err) {
        if (cancelled) {return;}
        setError(err instanceof Error ? err.message : String(err));
        setPhase('error');
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (phase === 'invalid') {
    return (
      <AuthShell
        title="Invalid link"
        subtitle="This approval link is malformed or missing its token."
      >
        <Link to="/auth/signin" className="om-approve__cta">
          Sign in
        </Link>
      </AuthShell>
    );
  }

  if (phase === 'expired') {
    return (
      <AuthShell
        title="Link expired"
        subtitle="This approval link is no longer valid — they're good for 5 minutes. Sign in again to get a fresh one."
      >
        <Link to="/auth/signin" className="om-approve__cta">
          Sign in again
        </Link>
      </AuthShell>
    );
  }

  if (phase === 'error') {
    return (
      <AuthShell
        title="Something went wrong"
        subtitle={error ?? 'We could not approve the sign-in.'}
      >
        <Link to="/auth/signin" className="om-approve__cta">
          Sign in again
        </Link>
      </AuthShell>
    );
  }

  if (phase === 'approved') {
    return (
      <AuthShell
        title="Approved"
        subtitle="Return to the device or browser tab where you started signing in — it'll finish there automatically. You can close this tab."
      >
        <Link to="/" className="om-approve__cta">
          Return home
        </Link>
      </AuthShell>
    );
  }

  // 'sending'
  return (
    <AuthShell title="Approving sign-in" subtitle="One moment…">
      <div className="om-approve__waiting" role="status" aria-live="polite">
        <span className="om-approve__pulse" aria-hidden="true" />
        <span className="om-approve__waiting-label">Approving</span>
      </div>
    </AuthShell>
  );
}
